// Semantic wrapping is collection-only; product-page subtitle markup is unchanged.
export function subtitleUnits(groups) {
  const units = [];
  for (const {kind, text} of groups) {
    let parts = [text.trim()];
    if (kind === 'shape') parts = text.trim().split(/\s+/);
    if (kind === 'color') {
      const match = text.trim().match(/^(Fancy(?: (?:Light|Intense|Vivid|Deep|Dark))?|Very Light|Light|Faint)\s+(.+)$/i);
      if (match) parts = [match[1], match[2]];
    }
    // Free-form overrides have no authoritative field boundaries. Preserve copy,
    // protecting recognizable phrases while leaving other words breakable.
    if (kind === 'override') parts = text.trim().match(/\d+(?:\.\d+)?\s+Carats?|Fancy\s+(?:Light|Intense|Vivid|Deep|Dark)|Very\s+Light|\w+ish\s+\w+|(?:Diamond|Sapphire|Ruby|Emerald)\s+(?:Ring|Pendant|Earrings?|Bracelet|Necklace)|\S+/gi) || [];
    parts.forEach((part, index) => units.push({text: part, soft: index > 0 && kind !== 'override'}));
  }
  return units;
}

export function subtitleLines(units, width, measure) {
  const best = Array(units.length + 1).fill(null);
  best[0] = {count: 0, cost: 0, lines: []};
  for (let end = 1; end <= units.length; end++) {
    for (let start = 0; start < end; start++) {
      if (!best[start]) continue;
      const text = units.slice(start, end).map(unit => unit.text).join(' ');
      const used = measure(text);
      if (used > width) continue;
      const widow = !text.includes(' ') && units.length > 1 ? 4 : 0;
      const split = start > 0 && units[start].soft ? 2 : 0;
      const candidate = {
        count: best[start].count + 1,
        cost: best[start].cost + (width - used) ** 2 + (widow + split) * width ** 2,
        lines: [...best[start].lines, text]
      };
      if (!best[end] || candidate.count < best[end].count ||
          (candidate.count === best[end].count && candidate.cost < best[end].cost)) best[end] = candidate;
    }
  }
  return best[units.length]?.lines || null;
}

// A shared frame keeps layout reads ahead of DOM writes across every subtitle.
const pending = new Set();
let frame;
function flushSubtitles() {
  frame = null;
  const jobs = [...pending].filter(node => node.isConnected);
  pending.clear();
  const layouts = jobs.map(node => node.readLayout()).filter(Boolean);
  for (const layout of layouts) layout.node.prepareMeasure(layout);
  for (const layout of layouts) layout.node.readMeasure(layout);
  for (const layout of layouts) layout.node.finishMeasure();
  for (const layout of layouts) layout.node.renderLines(layout);
}

if (typeof customElements !== 'undefined' && !customElements.get('rr-card-subtitle')) {
  customElements.define('rr-card-subtitle', class extends HTMLElement {
    connectedCallback() {
      if (!this.units) {
        this.units = subtitleUnits([...this.querySelectorAll('[data-subtitle-group]')].map(node => ({kind: node.dataset.subtitleGroup, text: node.textContent})));
        this.original = this.innerHTML;
      }
      this.observer?.disconnect();
      this.observer = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (width !== this.lastWidth) { this.lastWidth = width; this.schedule(); }
      });
      this.observer.observe(this);
      document.fonts.ready.then(() => { this.widths = null; if (this.isConnected) this.schedule(); });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      pending.delete(this);
      if (!pending.size) { cancelAnimationFrame(frame); frame = null; }
    }
    schedule() {
      pending.add(this);
      if (!frame) frame = requestAnimationFrame(flushSubtitles);
    }
    readLayout() {
      const width = this.getBoundingClientRect().width;
      if (!width) return null;
      const style = getComputedStyle(this);
      if (style.display === 'none') return null;
      const typography = [style.font, style.letterSpacing, style.textTransform].join('|');
      return {node: this, width, typography};
    }
    prepareMeasure({typography}) {
      if (typography === this.typography && this.widths) return;
      const ruler = document.createElement('span');
      ruler.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;width:max-content;pointer-events:none';
      this.probes = new Map();
      for (let start = 0; start < this.units.length; start++) {
        for (let end = start + 1; end <= this.units.length; end++) {
          const text = this.units.slice(start, end).map(unit => unit.text).join(' ');
          const probe = document.createElement('span');
          probe.style.cssText = 'display:block;width:max-content';
          probe.textContent = text;
          this.probes.set(text, probe);
          ruler.append(probe);
        }
      }
      this.ruler = ruler;
      this.append(ruler);
    }
    readMeasure({typography}) {
      if (!this.ruler) return;
      this.widths = new Map([...this.probes].map(([text, probe]) => [text, probe.getBoundingClientRect().width]));
      this.typography = typography;
    }
    finishMeasure() {
      this.ruler?.remove();
      this.ruler = null;
      this.probes = null;
    }
    renderLines({width}) {
      const lines = subtitleLines(this.units, width, text => this.widths.get(text));
      const signature = JSON.stringify(lines);
      if (signature === this.renderedLines) return;
      this.renderedLines = signature;
      if (!lines) { this.innerHTML = this.original; return; }
      this.replaceChildren(...lines.flatMap((text, index) => {
        const line = document.createElement('span');
        line.className = 'rr-card-subtitle__line';
        line.textContent = text;
        return index < lines.length - 1 ? [line, document.createTextNode(' ')] : [line];
      }));
    }
  });
}
